import { useState } from "react";
import { FaPlus, FaCheck, FaTimes } from "react-icons/fa";

/**
 * Katalog etiketi seçici (D-061) — kategori (tek) ve ürün ailesi (çoklu) için.
 * Liste panelden yönetilen etiketlerden gelir; buradan yeni etiket de eklenir
 * (sabit liste yok, yeni cins/hat gerektiğinde kod değişmeden büyür).
 *
 * @param {Array} labels - [{_id, name}]
 * @param {string|string[]} value - tekli: id | çoklu: [id]
 * @param {(v)=>void} onChange
 * @param {boolean} multi
 * @param {(name:string)=>Promise<{_id:string}|null>} onCreate - yeni etiket yarat
 */
export default function LabelPicker({
  labels = [],
  value,
  onChange,
  multi = false,
  onCreate,
  emptyHint = "Henüz tanım yok — aşağıdan ekleyebilirsin",
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const selected = multi ? value || [] : value ? [value] : [];
  const isOn = (id) => selected.includes(String(id));

  const toggle = (id) => {
    const sid = String(id);
    if (multi) {
      onChange(isOn(sid) ? selected.filter((x) => x !== sid) : [...selected, sid]);
    } else {
      onChange(isOn(sid) ? null : sid);
    }
  };

  const submitNew = async () => {
    const name = draft.trim();
    if (!name || !onCreate) return;
    setBusy(true);
    try {
      const created = await onCreate(name);
      if (created?._id) {
        const cid = String(created._id);
        onChange(multi ? [...selected, cid] : cid);
      }
      setDraft("");
      setAdding(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {labels.map((l) => (
        <button
          key={l._id}
          type="button"
          onClick={() => toggle(l._id)}
          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
            isOn(l._id)
              ? "bg-indigo-50 text-indigo-700 border-indigo-200"
              : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
          } ${l.isActive === false ? "opacity-50" : ""}`}
        >
          {l.name}
        </button>
      ))}

      {labels.length === 0 && !adding && (
        <span className="text-xs text-gray-400">{emptyHint}</span>
      )}

      {adding ? (
        <span className="inline-flex items-center gap-1">
          <input
            autoFocus
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitNew();
              }
              if (e.key === "Escape") setAdding(false);
            }}
            placeholder="Yeni ad"
            className="w-28 border border-input rounded-full px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200"
          />
          <button
            type="button"
            onClick={submitNew}
            disabled={!draft.trim() || busy}
            className="p-1 text-emerald-600 hover:text-emerald-800 disabled:opacity-40"
          >
            <FaCheck className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => {
              setAdding(false);
              setDraft("");
            }}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <FaTimes className="w-3 h-3" />
          </button>
        </span>
      ) : (
        onCreate && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border border-dashed border-gray-300 text-gray-500 hover:bg-gray-50"
          >
            <FaPlus className="w-2.5 h-2.5" /> Yeni
          </button>
        )
      )}
    </div>
  );
}
